// ============================================================================
// Shopee — signed-request helper.
//
// Every API call in `products.ts` / `orders.ts` / `stock.ts` / `logistics.ts`
// goes through `shopeeApiCall` so we have ONE place that:
//
//   1. Generates the timestamp + signature via `signShopeeRequest`.
//   2. Assembles the URL with auth + caller query params.
//   3. Issues the fetch with a JSON body (for POST).
//   4. Parses the response and, if Shopee reports an error either via HTTP
//      non-2xx OR via the 200-with-envelope path, classifies + throws
//      `ShopeeError`.
//
// ── SECURITY NOTE: access_token in URL query ───────────────────────────────
// Shopee REQUIRES `access_token` in the URL query string (by design — not a
// mistake). Shopee ignores the `Authorization` header entirely. This means
// any edge log that captures request URLs will capture access tokens verbatim.
//
// Operators MUST disable URL capture in Cloudflare Logpush / Workers Analytics
// for `partner.shopeemobile.com`. The recommended Logpush field filter is
// `ClientRequestURI = '*'` excluded on the connectors subroute. If the
// operator cannot disable URL capture at the platform level, they must
// accept that access tokens (valid for ~4 hours) will appear in Logpush.
// Partner keys + refresh tokens (30-day TTL) never appear in the URL.
//
// This file is the ONLY place in the Shopee adapter that builds
// `access_token=<value>` in a URL — keep it that way so a future `grep` for
// the pattern stays a single-callsite audit.
// ============================================================================

import {
  classifyShopeeError,
  parseShopeeRetryAfterMs,
  sanitizeShopeeErrorMessage,
  ShopeeError,
  type ShopeeErrorEnvelope,
} from './types'
import { signShopeeRequest } from './signing'

// ============================================================================
// Public types
// ============================================================================

export interface ShopeeApiInput {
  /** e.g. `"/api/v2/product/get_item_list"`. Must start with `/`. */
  apiPath: string
  /** Defaults to `GET`. */
  method?: 'GET' | 'POST'
  partnerId: string | number
  partnerKey: string
  accessToken: string
  shopId: string | number
  /** Extra query params merged after the auth params. Omitted / undefined values are dropped. */
  query?: Record<string, string | number | boolean | undefined>
  /** JSON body for POST calls. Ignored for GET. */
  body?: unknown
  /** Override the base — defaults to `https://partner.shopeemobile.com`. */
  apiBase?: string
  /** Injectable fetch for tests. */
  fetchImpl?: typeof fetch
  /** Pre-computed timestamp (test determinism). */
  timestampSec?: number
  /**
   * Optional hint: `'order'` for order-scoped endpoints so a 404 / not_found
   * classifies as `ORDER_NOT_FOUND` instead of the default `ITEM_NOT_FOUND`.
   */
  errorHint?: 'order' | 'item'
}

// ============================================================================
// Constants
// ============================================================================

export const SHOPEE_DEFAULT_API_BASE = 'https://partner.shopeemobile.com'

// ============================================================================
// shopeeApiCall — the one signed-request helper.
// ============================================================================

/**
 * Issue a signed Shopee Open Platform API call. Throws `ShopeeError` on any
 * non-2xx response OR any 200 that carries a non-empty `error` field in the
 * body envelope. Returns the parsed JSON body on success.
 *
 * The return type is generic so callers can assert the response shape:
 *
 *   const r = await shopeeApiCall<ShopeeItemListResponse>({ … })
 */
export async function shopeeApiCall<T>(input: ShopeeApiInput): Promise<T> {
  assertNonEmpty(input.apiPath, 'apiPath')
  if (!input.apiPath.startsWith('/')) {
    throw new ShopeeError('INVALID_INPUT', 'apiPath must start with "/"')
  }

  const method = input.method ?? 'GET'
  const apiBase = input.apiBase ?? SHOPEE_DEFAULT_API_BASE
  const fetchImpl = input.fetchImpl ?? fetch

  // 1. Sign.
  const signInput: Parameters<typeof signShopeeRequest>[0] = {
    partnerId: input.partnerId,
    partnerKey: input.partnerKey,
    apiPath: input.apiPath,
    accessToken: input.accessToken,
    shopId: input.shopId,
    ...(typeof input.timestampSec === 'number' ? { timestampSec: input.timestampSec } : {}),
  }
  const signed = await signShopeeRequest(signInput)

  // 2. Build URL — auth params first, then caller query. We use URLSearchParams
  // to ensure every value is URL-encoded; the path itself is included verbatim
  // because Shopee's API paths are stable identifiers (no user input).
  const url = new URL(apiBase + input.apiPath)
  for (const [k, v] of Object.entries(signed.queryParams)) {
    url.searchParams.set(k, v)
  }
  if (input.query) {
    for (const [k, v] of Object.entries(input.query)) {
      if (v === undefined) continue
      url.searchParams.set(k, String(v))
    }
  }

  // 3. Fetch.
  const headers: Record<string, string> = {
    Accept: 'application/json',
  }
  let body: BodyInit | undefined
  if (method === 'POST') {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(input.body ?? {})
  }

  let resp: Response
  try {
    resp = await fetchImpl(url.toString(), { method, headers, ...(body !== undefined ? { body } : {}) })
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    throw new ShopeeError('PROVIDER_ERROR', sanitizeShopeeErrorMessage(`fetch failed: ${msg}`))
  }

  // 4. Parse + classify.
  const text = await safeReadText(resp)
  let parsed: unknown = null
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text)
    } catch {
      // Fall through — parsed stays null; the error path below surfaces the
      // raw body (sanitised).
    }
  }

  const retryAfterMs = parseShopeeRetryAfterMs(resp.headers.get('retry-after'))

  if (!resp.ok) {
    const envelope = (parsed ?? null) as ShopeeErrorEnvelope | null
    // If the body was unparseable, build a synthetic envelope from the HTTP
    // status so downstream error classification still has something to work
    // with.
    const synthesised: ShopeeErrorEnvelope = envelope ?? {
      message: sanitizeShopeeErrorMessage(text).slice(0, 500),
    }
    throw classifyShopeeError(resp.status, synthesised, retryAfterMs, input.errorHint)
  }

  // 5. 200 with error envelope — Shopee's most footgunny behaviour. The
  // envelope's `error` field is the source of truth; an empty / missing
  // `error` means success.
  const envelope = (parsed ?? {}) as ShopeeErrorEnvelope
  if (typeof envelope.error === 'string' && envelope.error.length > 0) {
    throw classifyShopeeError(200, envelope, retryAfterMs, input.errorHint)
  }

  return envelope as unknown as T
}

// ============================================================================
// Helpers
// ============================================================================

function assertNonEmpty(value: string | number | undefined | null, field: string): void {
  if (value === undefined || value === null) {
    throw new ShopeeError('INVALID_INPUT', `${field} is required`)
  }
  if (typeof value === 'string' && value.length === 0) {
    throw new ShopeeError('INVALID_INPUT', `${field} must be a non-empty string`)
  }
}

async function safeReadText(resp: Response): Promise<string> {
  try {
    return await resp.text()
  } catch {
    return ''
  }
}
